import {
    buildOptionalParams,
    buildPath,
    buildTemplatePath,
    CrudMode,
    Dialog,
    handleClose,
    PageRoute,
    ScreenType,
    useParams,
    useUnsavedChanges,
    useValidationErrors,
} from "@compass/core";
import {
    ScholarshipPaymentMethod,
    ScholarshipPaymentMethodEditResource,
    ScholarshipPaymentMethodViewResource,
    Permission
} from "generated/openApi";
import { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { ScholarshipPaymentMethodPageHeader } from "./ScholarshipPaymentMethodPageHeader";
import { ScholarshipPaymentMethodPageBody } from "./ScholarshipPaymentMethodPageBody";

const routeName = "scholarshipPaymentMethod";

class Params {
    id: number = 0;
    crudMode: CrudMode = "view";
    screenType: ScreenType = "standard";
}

class OptionalParams {
    closeOnSave?: boolean = false;
}

export const scholarshipPaymentMethodPageRoute: PageRoute<Params, OptionalParams> = {
    handle: "Scholarship Payment Method",
    templatePath: buildTemplatePath(routeName, Params),
    getPath: (params) => buildPath(routeName, params),
    getOptionalParams: (params) => buildOptionalParams(params),
};

export type ScholarshipPaymentMethodContextValue = {
    onCreate?: (id: number) => void;
};

export const ScholarshipPaymentMethodContext = createContext<ScholarshipPaymentMethodContextValue>({} as ScholarshipPaymentMethodContextValue);

export const ScholarshipPaymentMethodPage = () => {
    const ROOT_PERMISSION: Permission = Permission.SystemUtility;
    const navigate = useNavigate();
    const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
    const [scholarshipPaymentMethod, setScholarshipPaymentMethod] = useState<ScholarshipPaymentMethod>({} as ScholarshipPaymentMethod);
    const [initScholarshipPaymentMethod, setInitScholarshipPaymentMethod] = useState<ScholarshipPaymentMethod>(scholarshipPaymentMethod);
    const [viewResource, setViewResource] = useState<ScholarshipPaymentMethodViewResource>({});
    const [editResource, setEditResource] = useState<ScholarshipPaymentMethodEditResource>({});
    const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [scholarshipPaymentMethod] });
    const { validationErrors, setValidationErrors } = useValidationErrors();
    const [closeOnSave] = useState<boolean>(optionalParams.closeOnSave || false);

    useEffect(() => {
        fetch(params.id);

        ApiService.scholarshipPaymentMethod.getViewResource().then((response) => {
            setViewResource(response);
        });
    }, []);

    useEffect(() => {
        if (params.crudMode === "edit") {
            ApiService.scholarshipPaymentMethod.getEditResource().then((response) => {
                setEditResource(response);
            });
        }
    }, [params.crudMode]);

    const reroute = (id: number, crudMode: CrudMode) =>
        navigate(`../${scholarshipPaymentMethodPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType, })}`);

    const fetch = (id: number) => {
        if (id > 0) {
            ApiService.scholarshipPaymentMethod.get(id).then((response) => {
                setScholarshipPaymentMethod(response);
                setInitScholarshipPaymentMethod(response);
                resetUnsavedChanges([response]);
            });
        } else {
            // For new
            setScholarshipPaymentMethod({} as ScholarshipPaymentMethod);
            setInitScholarshipPaymentMethod({} as ScholarshipPaymentMethod);
            resetUnsavedChanges([{} as ScholarshipPaymentMethod]);
        }
    };

    const pageHeader = (
        <ScholarshipPaymentMethodPageHeader
            rootPermission={ROOT_PERMISSION}
            handle={scholarshipPaymentMethodPageRoute.handle}
            crudMode={params.crudMode}
            reroute={reroute}
            fetch={fetch}
            scholarshipPaymentMethod={scholarshipPaymentMethod}
            setScholarshipPaymentMethod={setScholarshipPaymentMethod}
            initScholarshipPaymentMethod={initScholarshipPaymentMethod}
            setValidationErrors={setValidationErrors}
            unsavedChanges={unsavedChanges}
            resetUnsavedChanges={resetUnsavedChanges}
            closeOnSave={closeOnSave}
        />
    );

    const pageBody = (
        <ScholarshipPaymentMethodPageBody
            scholarshipPaymentMethod={scholarshipPaymentMethod}
            setScholarshipPaymentMethod={setScholarshipPaymentMethod}
            viewResource={viewResource}
            editResource={editResource}
            crudMode={params.crudMode}
            validationErrors={validationErrors}
        />
    );

    return (
        <Dialog
            isForm
            open={true}
            onClose={() => handleClose(navigate, unsavedChanges)}
            maxWidth="md"
            header={pageHeader}
            content={pageBody}
        />
    );
};
